/**
 * `/s/{slug}` — the public wrapper page for a web artifact (serving v2).
 *
 * Under v2 an artifact is served from THIS origin under `/a/{slug}/…` and is
 * never the top-level page: webgen 302s a bare navigation here, and this page
 * is the shareable address. It supplies the Chaidojo chrome, the honest
 * "generated content" label, and — for gated sites — the sign-in prompt.
 *
 * The iframe is belt-and-braces with webgen's own header: every `/a/*` HTML
 * response carries `Content-Security-Policy: sandbox …` (opaque origin, no
 * cookies, no storage) and this `sandbox` attribute repeats it. NEVER add
 * `allow-same-origin` — same-origin content with `allow-scripts` can remove
 * its own sandbox, and the artifact would then run on the CD origin.
 *
 * Deliberately OUTSIDE `_app` (the authed shell): a public site must render
 * for a signed-out visitor, exactly like `/invite/$token`.
 *
 * People a page was shared with by name (Manage → Share with people) open it
 * here on their own sign-in. For an artifact-only account the bar is the
 * whole of Chaidojo: the logo goes to its list of shared pages (`/shared`),
 * never into the product, and the bar carries its sign-out.
 *
 * State bridge: the framed page cannot store anything itself (opaque origin),
 * so `useArtifactStateBridge` answers its `chai.state` postMessages from here.
 * A signed-in visitor's state persists per (person, slug) — the SAME row the
 * artifact panel uses; a signed-out one gets the memory-only mode, which is
 * why the hook belongs on this public route too. See
 * `docs/agent/web-artifacts-state-bridge.md`.
 *
 * LLM bridge: `useArtifactLlmBridge` answers the page's `chai.complete` asks
 * the same way — with the visitor's own session, so a model answer is billed
 * to the person reading. A signed-out visitor is refused in a sentence
 * ("sign in to use this") rather than spending anyone else's budget. See
 * `docs/agent/web-artifacts-llm-bridge.md`.
 *
 * Data bridge: `useArtifactDataBridge` answers `chai.data` the same way, so a
 * page with no backend of its own can show live numbers from its workspace.
 * The gate is membership of THAT workspace, on every access level — a public
 * page opened by a stranger renders, and its data panel says so. See
 * `docs/agent/web-artifacts-data-bridge.md`.
 */

import { Wordmark } from "@chai/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useRef } from "react";
import { z } from "zod";

import { useArtifactDataBridge } from "@/hooks/useArtifactDataBridge";
import { useArtifactLlmBridge } from "@/hooks/useArtifactLlmBridge";
import { useArtifactSessionBridge } from "@/hooks/useArtifactSessionBridge";
import { useArtifactStateBridge } from "@/hooks/useArtifactStateBridge";
import { useAuth } from "@/auth";
import { useDocumentTitle } from "@/lib/document-title";

const SearchSchema = z.object({
  /** Capability token for `access="link"` sites, straight from the share URL. */
  k: z.string().optional(),
  /** Case platform: the case to open a solution's app on (`/s/{app}?case=…`, from the case page). The app reads
   * it from its own address, as its own links carry it. */
  case: z
    .string()
    .regex(/^[0-9A-Za-z]{6,64}$/)
    .optional()
    .catch(undefined),
});

/** The framed address with the case it opens on (none: unchanged). */
function withCase(url: string, caseId: string | undefined): string {
  if (!caseId) return url;
  const u = new URL(url, window.location.origin);
  u.searchParams.set("case", caseId);
  return u.origin === window.location.origin ? `${u.pathname}${u.search}${u.hash}` : u.toString();
}

export const Route = createFileRoute("/s/$slug")({
  validateSearch: (search) => SearchSchema.parse(search),
  component: WebArtifactWrapperPage,
});

interface ResolvedSite {
  access: string;
  /** Ready-to-frame artifact URL — token-carrying for gated sites. */
  url: string;
  title?: string | null;
  version?: number | null;
}

class ResolveError extends Error {
  constructor(readonly status: number) {
    super(`resolve failed (${status})`);
  }
}

async function resolveSite(slug: string, k?: string): Promise<ResolvedSite> {
  const params = new URLSearchParams({ site: slug });
  if (k) params.set("k", k);
  // Plain fetch, not the api client: this page must work with NO session, and
  // a 401 here is a state to render (sign-in prompt), not a failure to log.
  const resp = await fetch(`/api/webgen/resolve?${params.toString()}`, {
    credentials: "include",
  });
  if (!resp.ok) throw new ResolveError(resp.status);
  return (await resp.json()) as ResolvedSite;
}

function Shell({
  title,
  children,
}: {
  title?: string | null;
  children: React.ReactNode;
}) {
  const auth = useAuth();
  const artifactOnly = !!auth.me?.user.artifact_only;
  return (
    <div className="flex h-dvh min-h-0 flex-col bg-[var(--color-surface)]">
      <header className="flex shrink-0 items-center gap-3 border-b border-[var(--color-hairline)] bg-[var(--color-topbar)] px-4 py-2">
        <Wordmark href={artifactOnly ? "/shared" : "/"} />
        {title ? (
          <>
            <span className="text-[var(--color-muted)]" aria-hidden>
              /
            </span>
            <span className="min-w-0 truncate text-[13px] font-medium text-[var(--color-ink)]">
              {title}
            </span>
          </>
        ) : null}
        <span className="ml-auto shrink-0 rounded-full border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-2 py-0.5 text-[11px] text-[var(--color-slate)]">
          Generated content
        </span>
        {artifactOnly ? (
          <>
            <a
              href="/shared"
              className="shrink-0 text-[12px] text-[var(--color-slate)] no-underline hover:text-[var(--color-ink)]"
            >
              Shared with you
            </a>
            <SignOutButton />
          </>
        ) : null}
      </header>
      {children}
    </div>
  );
}

function SignOutButton({ label = "Sign out", then }: { label?: string; then?: string }) {
  const auth = useAuth();
  return (
    <button
      type="button"
      onClick={async () => {
        await auth.logout();
        window.location.href = then ?? "/signin";
      }}
      className="shrink-0 text-[12px] text-[var(--color-slate)] hover:text-[var(--color-ink)]"
    >
      {label}
    </button>
  );
}

function Notice({
  heading,
  body,
  action,
}: {
  heading: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <main className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-sm rounded-lg border border-[var(--color-hairline)] bg-[var(--color-bg-elevated)] p-6 text-center">
        <p className="text-[14px] font-medium text-[var(--color-ink)]">{heading}</p>
        <p className="mt-2 text-[13px] text-[var(--color-slate)]">{body}</p>
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </main>
  );
}

/** This page's own address (what sign-in returns to). */
function wrapperPath(slug: string, k: string | undefined, caseId: string | undefined): string {
  const q = new URLSearchParams();
  if (k) q.set("k", k);
  if (caseId) q.set("case", caseId);
  const qs = q.toString();
  return `/s/${encodeURIComponent(slug)}${qs ? `?${qs}` : ""}`;
}

function WebArtifactWrapperPage() {
  const auth = useAuth();
  const { slug } = useParams({ from: "/s/$slug" });
  const { k, case: caseId } = useSearch({ from: "/s/$slug" });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Keyed on the slug in the address bar — the same key the panel uses, so a
  // person who ticks a row here sees it ticked in Chaidojo.
  useArtifactStateBridge(iframeRef, slug);
  useArtifactLlmBridge(iframeRef, slug);
  useArtifactDataBridge(iframeRef, slug);

  const site = useQuery({
    queryKey: ["webgen-wrapper", slug, k ?? ""],
    queryFn: () => resolveSite(slug, k),
    // Space sites mint a viewer token per resolve; keep it well inside the
    // 60-minute TTL and don't retry the 401/403/404 states.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  // Keeps a gated page's viewer token fresh past its hour, in place — a
  // wrapper tab is exactly the page people leave open (`useArtifactSessionBridge`).
  const mintWrapperUrl = useCallback(
    async () => withCase((await resolveSite(slug, k)).url, caseId),
    [slug, k, caseId],
  );
  const framed = site.data ? withCase(site.data.url, caseId) : null;
  useArtifactSessionBridge(iframeRef, framed, mintWrapperUrl);
  // "‹App› · Chaidojo" — a wrapper opened full screen, pinned or bookmarked
  // says which app it is (lib/document-title).
  useDocumentTitle([site.data?.title]);

  if (site.isPending) {
    return (
      <Shell>
        <main className="flex flex-1 items-center justify-center">
          <p className="text-[13px] text-[var(--color-steel)]">Loading the page…</p>
        </main>
      </Shell>
    );
  }

  if (site.isError) {
    const status = site.error instanceof ResolveError ? site.error.status : 0;
    if (status === 401) {
      const returnTo = wrapperPath(slug, k, caseId);
      return (
        <Shell>
          <Notice
            heading="Sign in to view this page"
            body="This page is shared with named people and a Chaidojo workspace. Sign in with the account it was shared with."
            action={
              // Plain anchor, not <Link>: a full load re-runs the session
              // fetch, and `returnTo` is the sign-in route's own param.
              <a
                href={`/signin?returnTo=${encodeURIComponent(returnTo)}`}
                className="inline-flex items-center rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-[13px] font-medium text-[var(--color-on-primary)] no-underline hover:bg-[var(--color-primary-pressed)]"
              >
                Sign in
              </a>
            }
          />
        </Shell>
      );
    }
    if (status === 403) {
      const returnTo = wrapperPath(slug, k, caseId);
      return (
        <Shell>
          <Notice
            heading="You don't have access"
            body={
              auth.me
                ? `This page hasn't been shared with ${auth.me.user.email}. It is limited to the people it was shared with and members of its workspace.`
                : "This page is limited to the people it was shared with and members of its workspace."
            }
            action={
              auth.me ? (
                <SignOutButton
                  label="Sign in with another account"
                  then={`/signin?returnTo=${encodeURIComponent(returnTo)}`}
                />
              ) : undefined
            }
          />
        </Shell>
      );
    }
    return (
      <Shell>
        <Notice
          heading="This page isn't available"
          body={
            status === 404
              ? "The link may have been rotated, or the page archived."
              : "Something went wrong loading it. Try again in a moment."
          }
        />
      </Shell>
    );
  }

  return (
    <Shell title={site.data.title}>
      <iframe
        ref={iframeRef}
        src={framed ?? site.data.url}
        title={site.data.title ?? "Web artifact"}
        className="min-h-0 w-full flex-1 border-0 bg-white"
        // NEVER allow-same-origin (see the file header).
        sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads"
        referrerPolicy="no-referrer"
      />
    </Shell>
  );
}
